import { Box, useTheme, Typography, Grid } from "@mui/material";
import { useEffect, useCallback } from "react";
import ReactApexChart from "react-apexcharts";
import { LatestTrafficData } from "~src/pages/brands/Detail/TrafficAndRevenue";
import { colors } from "~src/data/brandTabColors.ts";
import { BrandResult } from "../SearchBar";
import { SkeletonCard } from "~src/components/SkeletonCard";
import usePermissions from "~src/hooks/usePermissions";

type PropsType = {
  graphData: (LatestTrafficData | undefined)[] | undefined;
  selectedBrands: BrandResult[];
  loading: boolean;
};

type ChartPropType = { values: number[]; keys: string[] };

export const TrafficChannelGraph = ({
                                      graphData,
                                      selectedBrands,
                                      loading,
                                    }: PropsType) => {
  const theme = useTheme();
  const { premiumCommunity } = usePermissions();

  // Memoize generateGraphData so its reference is stable between renders.
  const generateGraphData = useCallback(
    (
      graphData: (LatestTrafficData | undefined)[] | undefined,
    ): ChartPropType[] => {
      if (graphData) {
        return graphData.map((data: LatestTrafficData | undefined, index: number) => {
          if (data) {
            if (!premiumCommunity && (index === 3 || index === 4)) {
              return { values: [], keys: [] };
            }
            const keys = Object.keys(data);
            const values = Object.values(data).map((value) =>
              parseFloat(value?.replace("%", ""))
            );
            return { values, keys };
          }
          return { values: [], keys: [] };
        });
      }
      return [];
    },
    [premiumCommunity]
  );

  // If you don't need a side effect here, you can remove this useEffect.
  // Otherwise, if you need to run generateGraphData when graphData changes, keep it.
  useEffect(() => {
    generateGraphData(graphData);
  }, [generateGraphData, graphData]);

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.common.white,
        p: 4,
        borderRadius: "12px",
        mt: 2,
        border: `1px solid ${theme.palette.grey[200]}`,
      }}
    >
      <Typography variant="h5">Traffic Channels</Typography>
      <ApexChart
        graphData={generateGraphData(graphData)}
        selectedBrands={selectedBrands}
      />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mt: 2,
        }}
      />
      {loading &&
        Array(1)
          .fill(null)
          .map((_, index) => (
            <Grid key={index}  size={{ xs: 12, md: 12 }} >
              <SkeletonCard key={index} />
            </Grid>
          ))}
    </Box>
  );
};

interface ApexChartProps {
  graphData?: ChartPropType[];
  selectedBrands: BrandResult[];
}

const ApexChart: React.FC<ApexChartProps> = ({ graphData, selectedBrands }) => {
  const { premiumCommunity } = usePermissions();
  const series =
    graphData?.map((data, index) => ({
      name: selectedBrands[index]?.brand_name,
      data: data.values.map((value, i) => ({
        x: data.keys[i],
        y: value === null || isNaN(value) ? 0 : value,
        fillColor: colors[index % colors.length],
      })),
    })) || [];

  const options: ApexCharts.ApexOptions = {
    colors: colors,
    plotOptions: {
      bar: {
        borderRadius: 3,
        columnWidth: "75%",
        distributed: true,
        dataLabels: {
          position: "top",
          maxItems: 100,
        },
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      show: true,
      width: 2,
      colors: ["transparent"],
    },
    chart: {
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
    },
    xaxis: {
      categories: graphData ? graphData[0]?.keys : [],
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
    },
    yaxis: {
      labels: {
        formatter: (value: number) => `${value}%`,
      },
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
    },
    legend: {
      show: true,
      formatter: (seriesName: string, opts: any) => {
        const index = opts.seriesIndex;
        if (
          !premiumCommunity &&
          ((series.length === 5 &&
              (index === series.length - 2 || index === series.length - 1)) ||
            (series.length === 4 && index === series.length - 1))
        ) {
          return `<span style="position:relative;top:2px;"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 17C12.5523 17 13 16.5523 13 16C13 15.4477 12.5523 15 12 15C11.4477 15 11 15.4477 11 16C11 16.5523 11.4477 17 12 17ZM18 9V7C18 4.23858 15.7614 2 13 2C10.2386 2 8 4.23858 8 7V9H6V19H18V9H16ZM10 7C10 5.34315 11.3431 4 13 4C14.6569 4 16 5.34315 16 7V9H10V7Z" stroke="currentColor" fill="none"/>
        </svg></span>`;
        }
        return seriesName;
      },
    },
    grid: {
      show: true,
    },
    tooltip: {
      enabled: false,
    },
  };

  return (
    <ReactApexChart
      width={"100%"}
      height={"400px"}
      options={options}
      series={series}
      type="bar"
    />
  );
};
